// src/pages/dashboard/Payroll.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  DatePicker,
  Space,
  message,
  Tabs,
  Alert,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  DollarOutlined,
  CalendarOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  GiftOutlined,
  WalletOutlined,
  MinusCircleOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import PayrollDeductions from './PayrollDeductions';
import PayrollAllowances from './PayrollAllowances'; // NEW: Import allowances component
import PayslipPreview from './PayslipPreview';
import moment from 'moment';
import './Payroll.css';
import payrollService from '../../services/payrollService';
import payrollServiceSimple from '../../services/payrollServiceSimple'; // Simple
import { formatHoursMinutes } from '../../services/attendanceUtils';
import { patchPayrollMetadata } from '../../utils/patchPayrollMetadata';
import { Page, PageHeader, StatGrid, StatCard, StatusPill, EmptyState, formatRs } from '../../components/ui';

const { RangePicker } = DatePicker;

// e.g. "01 Sep 2026 - 30 Sep 2026" - the month name makes each period easy to spot
const formatPeriod = (start, end) =>
  `${moment(start).format('DD MMM YYYY')} - ${moment(end).format('DD MMM YYYY')}`;

// Short label for a period: "September 2026" for a whole month, otherwise "1 Sep - 15 Sep 2026"
const periodName = (start, end) => {
  const s = moment(start);
  const e = moment(end);
  if (s.isSame(s.clone().startOf('month'), 'day') && e.isSame(s.clone().endOf('month'), 'day')) {
    return s.format('MMMM YYYY');
  }
  return `${s.format('D MMM')} - ${e.format('D MMM YYYY')}`;
};

const PERIOD_STATUS_TONES = { pending: 'warning', processed: 'primary', paid: 'positive' };

const Payroll = () => {
  // State Management
  const [payrollSummary, setPayrollSummary] = useState({
    employeeCount: 0,
    totalRegularHours: 0,
    totalOvertimeHours: 0,
    totalHolidayHours: 0,
    totalGrossPay: 0,
    totalAllowances: 0,        // NEW: Track total allowances
    totalDeductions: 0,
    totalNetPay: 0
  });
  const [loading, setLoading] = useState(false);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [showDeductions, setShowDeductions] = useState(false);
  const [showAllowances, setShowAllowances] = useState(false); // NEW: State for allowances modal
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPayslip, setShowPayslip] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState('periods');
  const [deletingPeriod, setDeletingPeriod] = useState(null);
  // at the top of Payroll component
const recordsPanelRef = React.useRef(null);

useEffect(() => {
  if (activeTabKey === 'records') {
    // Move keyboard focus into the active tabpanel
    recordsPanelRef.current?.focus?.();
  }
}, [activeTabKey]);


  useEffect(() => {
    if (companyId) {
      console.log('Company ID:', companyId);
    }
  }, [companyId]);
  
  useEffect(() => {
    if (payrollRecords.length > 0) {
      console.log('Loaded Payroll Records:', payrollRecords);
    }
  }, [payrollRecords]);

  // Initial data fetch
  useEffect(() => {
    fetchCompanyId();
  }, []);

  // Update summary when records change
  useEffect(() => {
    if (payrollRecords.length > 0) {
      const newSummary = calculatePayrollSummary();
      setPayrollSummary(newSummary);
    }
  }, [payrollRecords]);

  // Fetch company ID and initial data
  const fetchCompanyId = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) throw userError;
    if (!user) {
      message.error('Please log in to continue');
      return;
    }

    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .select('id, name')
      .eq('user_id', user.id)
      .single();

    if (companyError) {
      if (companyError.code === 'PGRST116') {
        message.error('No company found for this user');
        return;
      }
      throw companyError;
    }

    if (!companyData) {
      message.error('Company information not found');
      return;
    }

    setCompanyId(companyData.id);
    await fetchPayrollPeriods(companyData.id);
  } catch (error) {
    console.error('Error fetching company:', error);
    message.error('Failed to load company information');
  }
};

  const fetchPayrollPeriods = async (company_id) => {
    if (!company_id) {
      console.error('Company ID is required');
      return;
    }
  
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('payroll_periods')
        .select(`
          id,
          start_date,
          end_date,
          status,
          processed_at,
          created_at,
          company_id
        `)
        .eq('company_id', company_id)
        .order('start_date', { ascending: false });
  
      if (error) throw error;
  
      setPayrollPeriods(data || []);
    } catch (error) {
      console.error('Error fetching payroll periods:', error);
      message.error('Failed to fetch payroll periods');
    } finally {
      setLoading(false);
    }
  };

  // ALSO UPDATE loadPayrollRecords to detect method:
const loadPayrollRecords = async (periodId) => {
  try {
    setLoading(true);

    // Detect method
    const { data: settings } = await supabase
      .from('settings')
      .select('payroll_settings')
      .eq('company_id', companyId)
      .single();

    const calculationMethod = settings?.payroll_settings?.overtime_calculation_method || 'daily';

    console.log('🔍 Loading payroll records with method:', calculationMethod);
    console.log('📋 Settings:', settings);

    let records;
    if (calculationMethod === 'simple') {
      console.log('✅ Using SIMPLE payroll service');
      records = await payrollServiceSimple.getPayrollRecords(periodId, companyId);
    } else {
      console.log('✅ Using STANDARD payroll service');
      records = await payrollService.getPayrollRecords(periodId, companyId);
    }
    
    if (records) {
      setPayrollRecords(records);
      setSelectedPeriod(periodId);
      setActiveTabKey('records');

      const summary = records.reduce((acc, record) => ({
        employeeCount: acc.employeeCount + 1,
        totalRegularHours: acc.totalRegularHours + (record.regular_hours || 0),
        totalOvertimeHours: acc.totalOvertimeHours + (record.overtime_hours || 0),
        totalHolidayHours: acc.totalHolidayHours + (record.holiday_hours || 0),
        totalGrossPay: acc.totalGrossPay + (record.gross_pay || 0),
        totalAllowances: acc.totalAllowances + (record.total_allowances || 0),
        totalDeductions: acc.totalDeductions + (record.total_deductions || 0),
        totalNetPay: acc.totalNetPay + (record.net_pay || 0)
      }), {
        employeeCount: 0,
        totalRegularHours: 0,
        totalOvertimeHours: 0,
        totalHolidayHours: 0,
        totalGrossPay: 0,
        totalAllowances: 0,
        totalDeductions: 0,
        totalNetPay: 0
      });

      setPayrollSummary(summary);
    }
  } catch (error) {
    console.error('Error loading payroll records:', error);
    message.error('Failed to load payroll records');
  } finally {
    setLoading(false);
  }
};


const handleCreatePayroll = async () => {
  try {
    if (!dateRange || dateRange.length !== 2) {
      message.error('Please select a date range');
      return;
    }

    if (!companyId) {
      message.error('Company information not found');
      return;
    }

    setLoading(true);
    const [startDate, endDate] = dateRange;
    const formattedStartDate = startDate.format('YYYY-MM-DD');
    const formattedEndDate = endDate.format('YYYY-MM-DD');

    // Check for existing period
    const existingPeriods = await payrollService.getPayrollPeriods(companyId);
    const periodExists = existingPeriods.some(period => 
      moment(period.start_date).isSame(formattedStartDate, 'day') &&
      moment(period.end_date).isSame(formattedEndDate, 'day')
    );

    if (periodExists) {
      message.error('A payroll period already exists for the selected date range');
      return;
    }

    // DETECT CALCULATION METHOD FROM SETTINGS
    const { data: settings, error: settingsError } = await supabase
      .from('settings')
      .select('payroll_settings')
      .eq('company_id', companyId)
      .single();

    if (settingsError && settingsError.code !== 'PGRST116') {
      console.error('Settings error:', settingsError);
    }

    const calculationMethod = settings?.payroll_settings?.overtime_calculation_method || 'daily';
    
    console.log('Using calculation method:', calculationMethod);

    // Choose the appropriate service
    let payrollRecords;
    if (calculationMethod === 'simple') {
      // Use simple calculation for Aux Champs Elysees
      message.info('Using Simple Payroll Calculation Method');
      payrollRecords = await payrollServiceSimple.calculatePayrollSimple(
        formattedStartDate,
        formattedEndDate,
        companyId
      );
    } else {
      // Use complex calculation (daily or monthly)
      message.info('Using Standard Payroll Calculation Method');
      payrollRecords = await payrollService.calculatePayroll(
        formattedStartDate,
        formattedEndDate,
        companyId
      );
    }

    if (payrollRecords && payrollRecords.length > 0) {
      // Refresh the periods list
      const updatedPeriods = await payrollService.getPayrollPeriods(companyId);
      setPayrollPeriods(updatedPeriods);
      
      const newPeriod = updatedPeriods[0];
      
      if (newPeriod) {
        setSelectedPeriod(newPeriod.id);
        
        // Load records using the appropriate service
        if (calculationMethod === 'simple') {
          await loadPayrollRecordsSimple(newPeriod.id);
        } else {
          await loadPayrollRecords(newPeriod.id);
        }
        
        setActiveTabKey('records');
        
        message.success('Payroll period created successfully');
      }
    } else {
      message.warning('No payroll records were created. Please check the date range and employee data.');
    }

  } catch (error) {
    console.error('Payroll creation error:', error);
    message.error(error.message || 'Error creating payroll period');
  } finally {
    setLoading(false);
  }
};
  
// ADD NEW FUNCTION for loading simple payroll records
const loadPayrollRecordsSimple = async (periodId) => {
  try {
    setLoading(true);
    
    const records = await payrollServiceSimple.getPayrollRecords(periodId, companyId);
    
    if (records) {
      setPayrollRecords(records);
      setSelectedPeriod(periodId);
      setActiveTabKey('records');

      const summary = records.reduce((acc, record) => ({
        employeeCount: acc.employeeCount + 1,
        totalRegularHours: acc.totalRegularHours + (record.regular_hours || 0),
        totalOvertimeHours: acc.totalOvertimeHours + (record.overtime_hours || 0),
        totalHolidayHours: acc.totalHolidayHours + (record.holiday_hours || 0),
        totalGrossPay: acc.totalGrossPay + (record.gross_pay || 0),
        totalAllowances: acc.totalAllowances + (record.total_allowances || 0),
        totalDeductions: acc.totalDeductions + (record.total_deductions || 0),
        totalNetPay: acc.totalNetPay + (record.net_pay || 0)
      }), {
        employeeCount: 0,
        totalRegularHours: 0,
        totalOvertimeHours: 0,
        totalHolidayHours: 0,
        totalGrossPay: 0,
        totalAllowances: 0,
        totalDeductions: 0,
        totalNetPay: 0
      });

      setPayrollSummary(summary);
    }
  } catch (error) {
    console.error('Error loading payroll records:', error);
    message.error('Failed to load payroll records');
  } finally {
    setLoading(false);
  }
};

  const handleProcessPayroll = async (periodId) => {
    try {
      setLoading(true);
      
      const { error: updateError } = await supabase
        .from('payroll_records')
        .update({
          status: 'processed',
          updated_at: new Date().toISOString()
        })
        .eq('period_id', periodId)
        .eq('company_id', companyId);

      if (updateError) throw updateError;

      const { error: periodError } = await supabase
        .from('payroll_periods')
        .update({
          status: 'processed',
          processed_at: new Date().toISOString()
        })
        .eq('id', periodId)
        .eq('company_id', companyId);

      if (periodError) throw periodError;

      message.success('Payroll processed successfully');
      await fetchPayrollPeriods(companyId);
      if (selectedPeriod === periodId) {
        await loadPayrollRecords(periodId);
      }
    } catch (error) {
      message.error('Error processing payroll');
      console.error('Process payroll error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (periodId) => {
    try {
      setLoading(true);
      
      const { error: updateError } = await supabase
        .from('payroll_periods')
        .update({
          status: 'paid',
          updated_at: new Date().toISOString()
        })
        .eq('id', periodId)
        .eq('company_id', companyId);

      if (updateError) throw updateError;

      const { error: recordsError } = await supabase
        .from('payroll_records')
        .update({
          status: 'paid',
          updated_at: new Date().toISOString()
        })
        .eq('period_id', periodId)
        .eq('company_id', companyId);

      if (recordsError) throw recordsError;

      message.success('Payroll marked as paid');
      await fetchPayrollPeriods(companyId);
      if (selectedPeriod === periodId) {
        await loadPayrollRecords(periodId);
      }
    } catch (error) {
      message.error('Error marking payroll as paid');
      console.error('Mark as paid error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageDeductions = (record) => {
    // Pass both the payroll record and employee info
    const employeeData = {
      ...record,
      employeeId: record.employee_id, // Add this for clarity
      payrollRecordId: record.id,     // Keep this for updating the payroll record
    };
    setSelectedEmployee(employeeData);
    setShowDeductions(true);
  };

  // NEW: Handle manage allowances
  const handleManageAllowances = (record) => {
    const employeeData = {
      ...record,
      employeeId: record.employee_id,
      payrollRecordId: record.id,
    };
    setSelectedEmployee(employeeData);
    setShowAllowances(true);
  };

  const handleViewPayslip = (record) => {
    // Find selected period to get dates
    const periodData = payrollPeriods.find(p => p.id === selectedPeriod);
    
    // Prepare employee data with period dates
    const employeeWithPeriod = {
      ...record,
      period_start: periodData?.start_date,
      period_end: periodData?.end_date,
      company_id: companyId // Make sure company_id is included
    };
    
    setSelectedEmployee(employeeWithPeriod);
    setShowPayslip(true);
  };

  const handleDeletePayroll = async (periodId) => {
    try {
      setDeletingPeriod(periodId);
      setLoading(true);

      console.log('🗑️ Starting delete for period:', periodId);

      // First delete all payroll records for this period
      console.log('🗑️ Step 1: Deleting payroll records...');
      const { data: deletedRecords, error: recordsError } = await supabase
        .from('payroll_records')
        .delete()
        .eq('period_id', periodId)
        .eq('company_id', companyId)
        .select();

      if (recordsError) {
        console.error('❌ Error deleting payroll records:', recordsError);
        throw new Error(`Failed to delete payroll records: ${recordsError.message}`);
      }

      console.log(`✅ Deleted ${deletedRecords?.length || 0} payroll records`);

      // Then delete the payroll period
      console.log('🗑️ Step 2: Deleting payroll period...');
      const { data: deletedPeriod, error: periodError } = await supabase
        .from('payroll_periods')
        .delete()
        .eq('id', periodId)
        .eq('company_id', companyId)
        .select();

      if (periodError) {
        console.error('❌ Error deleting payroll period:', periodError);
        throw new Error(`Failed to delete payroll period: ${periodError.message}`);
      }

      // RLS can silently block a delete (no error, zero rows) - don't report success in that case
      if (!deletedPeriod || deletedPeriod.length === 0) {
        throw new Error('Payroll period was not deleted - you may not have permission to delete it.');
      }

      console.log('✅ Payroll period deleted successfully:', deletedPeriod);

      message.success('Payroll period deleted successfully');

      // Clear selected period if it was deleted
      if (selectedPeriod === periodId) {
        setSelectedPeriod(null);
        setPayrollRecords([]);
      }

      // Refresh the list of periods
      await fetchPayrollPeriods(companyId);

      // Reset to periods tab if the selected period was deleted
      if (selectedPeriod === periodId) {
        setActiveTabKey('periods');
      }

    } catch (error) {
      console.error('❌ Error deleting payroll period:', error);
      message.error(error.message || 'Failed to delete payroll period');
    } finally {
      setLoading(false);
      setDeletingPeriod(null);
    }
  };

  const [refreshingPeriod, setRefreshingPeriod] = useState(null);

  const handleRefreshPayroll = async (periodId) => {
    try {
      setRefreshingPeriod(periodId);
      setLoading(true);

      // Detect calculation method
      const { data: settings } = await supabase
        .from('settings')
        .select('payroll_settings')
        .eq('company_id', companyId)
        .single();

      const calculationMethod = settings?.payroll_settings?.overtime_calculation_method || 'daily';

      message.loading('Recalculating payroll...', 0);

      let records;
      if (calculationMethod === 'simple') {
        records = await payrollServiceSimple.recalculatePayrollPeriod(periodId, companyId);
      } else {
        records = await payrollService.recalculatePayrollPeriod(periodId, companyId);
      }

      message.destroy();

      if (records && records.length > 0) {
        message.success(`Payroll recalculated successfully (${records.length} employees)`);

        // Reload the records if this period is currently selected
        if (selectedPeriod === periodId) {
          await loadPayrollRecords(periodId);
        }
      } else {
        message.warning('No records were generated during recalculation');
      }
    } catch (error) {
      message.destroy();
      console.error('Error recalculating payroll:', error);
      message.error(error.message || 'Failed to recalculate payroll');
    } finally {
      setLoading(false);
      setRefreshingPeriod(null);
    }
  };

  const handlePatchMetadata = async () => {
    if (!selectedPeriod || !companyId) {
      message.error('Please select a payroll period first');
      return;
    }

    try {
      setLoading(true);
      message.loading('Patching payroll metadata...', 0);

      const result = await patchPayrollMetadata(selectedPeriod, companyId);

      message.destroy(); // Clear the loading message

      if (result.success) {
        message.success(result.message);
        // Reload the payroll records to show updated data
        await loadPayrollRecords(selectedPeriod);
      } else {
        message.error(result.message || 'Failed to patch metadata');
      }
    } catch (error) {
      message.destroy();
      console.error('Error patching metadata:', error);
      message.error('Failed to patch payroll metadata');
    } finally {
      setLoading(false);
    }
  };

  const calculatePayrollSummary = () => {
    return payrollRecords.reduce((summary, record) => ({
      employeeCount: new Set(payrollRecords.map(r => r.employee_id)).size,
      totalRegularHours: summary.totalRegularHours + (record.regular_hours || 0),
      totalOvertimeHours: summary.totalOvertimeHours + (record.overtime_hours || 0),
      totalHolidayHours: summary.totalHolidayHours + (record.holiday_hours || 0),
      totalGrossPay: summary.totalGrossPay + (record.gross_pay || 0),
      totalAllowances: summary.totalAllowances + (record.total_allowances || 0), // NEW
      totalDeductions: summary.totalDeductions + (record.total_deductions || 0),
      totalNetPay: summary.totalNetPay + (record.net_pay || 0)
    }), {
      employeeCount: 0,
      totalRegularHours: 0,
      totalOvertimeHours: 0,
      totalHolidayHours: 0,
      totalGrossPay: 0,
      totalAllowances: 0, // NEW
      totalDeductions: 0,
      totalNetPay: 0
    });
  };

  const refreshPayrollRecord = async (employeeId) => {
    try {
      if (!selectedPeriod) return;

      const { data, error } = await supabase
        .from('payroll_records')
        .select(`
          *,
          employees (
            id,
            first_name,
            last_name,
            department,
            position,
            employee_number
          )
        `)
        .eq('id', employeeId)
        .single();

      if (error) throw error;

      setPayrollRecords(prevRecords =>
        prevRecords.map(record =>
          record.id === employeeId ? data : record
        )
      );
    } catch (error) {
      message.error('Error refreshing payroll record');
      console.error('Refresh record error:', error);
    }
  };

  const periodColumns = [
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <span className="stack-cell">
          <span style={{ fontWeight: 600 }}>{periodName(record.start_date, record.end_date)}</span>
          <span className="stack-cell__sub">{formatPeriod(record.start_date, record.end_date)}</span>
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => (
        <StatusPill tone={PERIOD_STATUS_TONES[status] || 'neutral'} solid={status === 'paid'}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        </StatusPill>
      ),
    },
    {
      title: 'Processed',
      dataIndex: 'processed_at',
      key: 'processed_at',
      width: 150,
      render: (date) => (date ? moment(date).format('DD MMM YYYY') : <span className="text-muted">Not yet</span>),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space size={6} wrap style={{ justifyContent: 'flex-end' }}>
          <Button
            type={record.id === selectedPeriod ? 'primary' : 'default'}
            onClick={() => {
              loadPayrollRecords(record.id);
              setActiveTabKey('records'); // Explicitly set tab
            }}
          >
            {record.id === selectedPeriod ? 'Viewing' : 'View details'}
          </Button>

          {record.status === 'pending' && (
            <Button
              type="primary"
              onClick={() => handleProcessPayroll(record.id)}
              loading={loading && selectedPeriod === record.id && !deletingPeriod}
              disabled={loading}
            >
              Process payroll
            </Button>
          )}

          {record.status === 'processed' && (
            <Button
              onClick={() => handleMarkAsPaid(record.id)}
              icon={<CheckCircleOutlined />}
              disabled={loading}
            >
              Mark as paid
            </Button>
          )}

          {/* Refresh button - only show for pending payrolls */}
          {record.status === 'pending' && (
            <Popconfirm
              title="Recalculate this payroll period?"
              description="This will delete all existing records and recalculate from the latest timesheet data."
              onConfirm={() => handleRefreshPayroll(record.id)}
              okText="Yes, Recalculate"
              cancelText="No"
            >
              <Tooltip title="Recalculate from the latest timesheet data">
                <Button
                  type="text"
                  className="icon-action"
                  data-tone="primary"
                  aria-label="Recalculate payroll"
                  icon={<ReloadOutlined />}
                  loading={loading && refreshingPeriod === record.id}
                  disabled={loading && refreshingPeriod !== record.id}
                />
              </Tooltip>
            </Popconfirm>
          )}

          {/* Delete button - all statuses; processed/paid get a stronger warning naming the period */}
          {['pending', 'processed', 'paid'].includes(record.status) && (
            <Popconfirm
              title={record.status === 'pending'
                ? 'Delete this payroll period?'
                : `Delete ${record.status.toUpperCase()} payroll ${formatPeriod(record.start_date, record.end_date)}?`}
              description={record.status === 'pending'
                ? 'This will permanently delete this payroll period and all related records. This action cannot be undone.'
                : `This payroll is already marked as ${record.status}. Deleting it permanently removes the period and all employee payroll records and payslips. Employee allowances and deductions are kept and will be applied again when you create the payroll for these dates.`}
              onConfirm={() => handleDeletePayroll(record.id)}
              okText="Yes, Delete"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete payroll period">
                <Button
                  type="text"
                  className="icon-action"
                  data-tone="danger"
                  aria-label="Delete payroll period"
                  icon={<DeleteOutlined />}
                  loading={loading && deletingPeriod === record.id}
                  disabled={loading && deletingPeriod !== record.id}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const recordColumns = [
  {
    title: 'Employee',
    key: 'employee',
    fixed: 'left',
    width: 190,
    render: (_, record) => (
      <span className="stack-cell">
        <span style={{ fontWeight: 600 }}>
          {`${record.employees?.first_name || ''} ${record.employees?.last_name || ''}`}
        </span>
        <span className="stack-cell__sub">
          {record.employees?.department || 'Unassigned'}
        </span>
      </span>
    ),
  },
  {
    title: 'Net pay',
    dataIndex: 'net_pay',
    width: 120,
    align: 'right',
    render: (amount) => (
      <span className={`num ${amount >= 0 ? 'num--strong' : 'num--negative'}`}>
        {formatRs(amount)}
      </span>
    ),
  },
  {
    title: 'Regular',
    dataIndex: 'regular_hours',
    width: 100,
    align: 'center',
    render: (hours) => <span className="num">{formatHoursMinutes(hours || 0)}</span>,
  },
  {
    title: 'Overtime',
    dataIndex: 'overtime_hours',
    width: 100,
    align: 'center',
    render: (hours) => (
      <span className={`num ${hours > 0 ? 'num--primary' : 'num--muted'}`}>
        {formatHoursMinutes(hours || 0)}
      </span>
    ),
  },
  {
    title: 'Holiday',
    dataIndex: 'holiday_hours',
    width: 100,
    align: 'center',
    render: (hours) => (
      <span className={`num ${hours > 0 ? 'num--primary' : 'num--muted'}`}>
        {formatHoursMinutes(hours || 0)}
      </span>
    ),
  },
  {
    title: 'Allowances',
    dataIndex: 'total_allowances',
    width: 110,
    align: 'right',
    render: (amount) => (
      <span className={`num ${amount > 0 ? 'num--positive' : 'num--muted'}`}>
        {amount > 0 ? `+${formatRs(amount)}` : formatRs(0)}
      </span>
    ),
  },
  {
    title: 'Gross pay',
    dataIndex: 'gross_pay',
    width: 120,
    align: 'right',
    render: (amount) => <span className="num" style={{ fontWeight: 600 }}>{formatRs(amount)}</span>,
  },
  {
    title: 'Deductions',
    dataIndex: 'total_deductions',
    width: 110,
    align: 'right',
    render: (amount) => (
      <span className={`num ${amount > 0 ? 'num--negative' : 'num--muted'}`}>
        {amount > 0 ? `-${formatRs(amount)}` : formatRs(0)}
      </span>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 120,
    align: 'center',
    render: (status) => (
      <StatusPill tone={PERIOD_STATUS_TONES[status] || 'neutral'} solid={status === 'paid'}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </StatusPill>
    )
  },
  {
    title: '',
    key: 'actions',
    fixed: 'right',
    width: 120,
    align: 'center',
    render: (_, record) => (
      <Space size={2}>
        <Tooltip title="Manage allowances">
          <Button
            type="text"
            className="icon-action"
            data-tone="positive"
            aria-label="Manage allowances"
            icon={<GiftOutlined />}
            onClick={() => handleManageAllowances(record)}
          />
        </Tooltip>

        <Tooltip title="Manage deductions">
          <Button
            type="text"
            className="icon-action"
            data-tone="danger"
            aria-label="Manage deductions"
            icon={<MinusCircleOutlined />}
            onClick={() => handleManageDeductions(record)}
          />
        </Tooltip>

        <Tooltip title="View payslip">
          <Button
            type="text"
            className="icon-action"
            data-tone="primary"
            aria-label="View payslip"
            icon={<FilePdfOutlined />}
            onClick={() => handleViewPayslip(record)}
          />
        </Tooltip>
      </Space>
    )
  }
];

// Inside your Payroll component
const getTabItems = () => {
  const baseItems = [
    {
      key: 'periods',
      label: (
        <span>
          <CalendarOutlined /> Payroll periods
        </span>
      ),
      children: (
        <Table
          columns={periodColumns}
          dataSource={payrollPeriods}
          rowKey="id"
          loading={loading}
          size="middle"
          rowClassName={(record) => record.id === selectedPeriod ? 'payroll-period-selected' : ''}
          scroll={{ x: 760 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} periods`
          }}
          locale={{
            emptyText: (
              <EmptyState
                plain
                icon={<CalendarOutlined />}
                title="No payroll periods yet"
                description="Pick the period dates above and choose Create payroll to calculate pay from attendance."
              />
            )
          }}
        />
      )
    }
  ];

  if (selectedPeriod) {
    baseItems.push({
      key: 'records',
      label: (
        <span>
          <TeamOutlined /> Employee pay
        </span>
      ),
      // ✅ Make wrapper focusable and attach the ref
      children: (
        <div ref={recordsPanelRef} tabIndex={-1} style={{ outline: 'none' }}>
          {/* Patch Metadata Button */}
          <Alert
            message="Payslips showing 0 working days?"
            description="Older payroll records may be missing working-day details. Fix them for this period without recalculating pay."
            type="info"
            showIcon
            action={
              <Button
                size="small"
                onClick={handlePatchMetadata}
                loading={loading}
              >
                Fix metadata
              </Button>
            }
            closable
            style={{ margin: '4px 8px 12px' }}
          />

          <Table
            columns={recordColumns}
            dataSource={payrollRecords}
            rowKey="id"
            loading={loading}
            size="middle"
            scroll={{ x: 1200 }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} employees`
            }}
            summary={(pageData) => {
              const totals = pageData.reduce((acc, record) => ({
                regularHours: acc.regularHours + (record.regular_hours || 0),
                overtimeHours: acc.overtimeHours + (record.overtime_hours || 0),
                holidayHours: acc.holidayHours + (record.holiday_hours || 0),
                allowances: acc.allowances + (record.total_allowances || 0),
                grossPay: acc.grossPay + (record.gross_pay || 0),
                deductions: acc.deductions + (record.total_deductions || 0),
                netPay: acc.netPay + (record.net_pay || 0)
              }), {
                regularHours: 0,
                overtimeHours: 0,
                holidayHours: 0,
                allowances: 0,
                grossPay: 0,
                deductions: 0,
                netPay: 0
              });

              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>Page totals</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <span className="num num--strong">{formatRs(totals.netPay)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      <span className="num">{formatHoursMinutes(totals.regularHours)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="center">
                      <span className="num">{formatHoursMinutes(totals.overtimeHours)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="center">
                      <span className="num">{formatHoursMinutes(totals.holidayHours)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <span className="num num--positive">{formatRs(totals.allowances)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <span className="num">{formatRs(totals.grossPay)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      <span className="num num--negative">{formatRs(totals.deductions)}</span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} />
                    <Table.Summary.Cell index={9} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>
      )
    });
  }

  return baseItems;
};

  const selectedPeriodData = payrollPeriods.find(p => p.id === selectedPeriod);
  const summaryCaption = selectedPeriodData
    ? periodName(selectedPeriodData.start_date, selectedPeriodData.end_date)
    : 'Open a period to see totals';

  return (
    <Page>
      <PageHeader
        title="Payroll"
        subtitle="Create a pay period from attendance, review each employee's pay, then process it and mark it as paid."
        actions={
          <>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD/MM/YYYY"
              placeholder={['Period start', 'Period end']}
            />
            <Button
              type="primary"
              onClick={handleCreatePayroll}
              loading={loading && !deletingPeriod}
              disabled={!dateRange || dateRange.length !== 2 || loading}
              icon={<PlusOutlined />}
            >
              Create payroll
            </Button>
          </>
        }
      />

      {/* Summary for the open period */}
      <StatGrid columns={5}>
        <StatCard
          label="Employees"
          tone="primary"
          icon={<TeamOutlined />}
          value={payrollSummary.employeeCount}
          caption={summaryCaption}
        />
        <StatCard
          label="Gross pay"
          tone="neutral"
          icon={<WalletOutlined />}
          value={formatRs(payrollSummary.totalGrossPay)}
          caption="Before deductions"
          compact
        />
        <StatCard
          label="Allowances"
          tone="positive"
          icon={<GiftOutlined />}
          value={formatRs(payrollSummary.totalAllowances)}
          caption="Included in gross pay"
          compact
        />
        <StatCard
          label="Deductions"
          tone="danger"
          icon={<MinusCircleOutlined />}
          value={formatRs(payrollSummary.totalDeductions)}
          caption="Taken off gross pay"
          compact
        />
        <StatCard
          label="Net pay"
          tone="positive"
          icon={<DollarOutlined />}
          value={formatRs(payrollSummary.totalNetPay)}
          caption="Total to pay employees"
          compact
        />
      </StatGrid>

      {/* Periods and records */}
      <Card className="table-card">
        <Tabs
          activeKey={activeTabKey}
          onChange={(key) => setActiveTabKey(key)}
          items={getTabItems()}
          tabBarExtraContent={
            selectedPeriodData && (
              <span className="chip" style={{ marginRight: 8 }}>
                <CalendarOutlined /> {formatPeriod(selectedPeriodData.start_date, selectedPeriodData.end_date)}
              </span>
            )
          }
          tabBarStyle={{ padding: '0 12px', marginBottom: 8 }}
        />
      </Card>

      {/* Modals */}
      <PayrollDeductions
        visible={showDeductions}
        employee={selectedEmployee}
        onClose={async () => {
          if (selectedEmployee) {
            await refreshPayrollRecord(selectedEmployee.id);
          }
          setShowDeductions(false);
          setSelectedEmployee(null);
        }}
        companyId={companyId}
      />

      {/* NEW: Allowances Modal */}
      <PayrollAllowances
        visible={showAllowances}
        employee={selectedEmployee}
        onClose={async () => {
          if (selectedEmployee) {
            await loadPayrollRecords(selectedPeriod); // Refresh all records to get updated totals
          }
          setShowAllowances(false);
          setSelectedEmployee(null);
        }}
        companyId={companyId}
      />

      <PayslipPreview
        visible={showPayslip}
        employee={selectedEmployee}
        onClose={() => {
          setShowPayslip(false);
          setSelectedEmployee(null);
        }}
      />
    </Page>
  );
};

export default Payroll;
